import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CreatableSelect from 'react-select/creatable';
import { Search, Plus } from 'lucide-react';
import DeviceRegistrationModal from './MedicalDeviceRegistration';
import MedicalDeviceDetailsModal from './MedicalDeviceDetailsModal';
import ZoomableImage from '../components/ZoomableImage';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const PLACEHOLDER_IMG = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

// --- Global Compact Table Styles ---
const globalCss = `
  .ctbl th, .ctbl td { padding: 8px 10px !important; font-size: 0.78rem !important; border: 1px solid #c5cbd3 !important; vertical-align: middle !important; white-space: nowrap; }
  .ctbl th { padding: 10px 10px !important; font-size: 0.78rem !important; text-transform: uppercase; letter-spacing: 0.04em; color: #1a3a5c; font-weight: 700; background: #86afd8 !important; text-align: center; }
  .ctbl thead { background: #86afd8 !important; }
  .ctbl { table-layout: fixed; width: 100% !important; }
  .ctbl td.wc { white-space: normal !important; }
`;

const fmtDate = (d?: string | null) => {
  if (!d) return '-';
  const [y, m, day] = d.split('T')[0].split('-');
  return `${day}-${m}-${y}`;
};

const MedicalDevicesTab = () => {
  const [devices, setDevices] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hoverRowId, setHoverRowId] = useState<number | null>(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [editingDevice, setEditingDevice] = useState<any | null>(null);
  const [viewDevice, setViewDevice] = useState<any | null>(null);

  // Inline category / priority editing
  const [editingCatId, setEditingCatId] = useState<number | null>(null);
  const [savingCat, setSavingCat]       = useState(false);
  const [editingPriId, setEditingPriId] = useState<number | null>(null);
  const [savingPri, setSavingPri]       = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

  const fetchDevices = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/medical-devices/`, { headers: authHeaders() });
      setDevices(res.data);
    } catch (err) {
      toast.error("Failed to load devices");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchDevices(); }, []);

  const categoryOptions = Array.from(
    new Set(devices.map(d => d.category).filter(Boolean) as string[])
  ).sort((a, b) => a.localeCompare(b));

  // PATCH a single catalog field (category / priority) via the device_data multipart payload.
  const patchDeviceField = async (id: number, patch: Record<string, unknown>) => {
    const fd = new FormData();
    fd.append('device_data', JSON.stringify(patch));
    await axios.patch(`${API_URL}/api/medical-devices/${id}`, fd, {
      headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    });
  };

  const saveCategory = async (id: number, value: string) => {
    if (savingCat) return;
    setSavingCat(true);
    try {
      await patchDeviceField(id, { category: value.trim() || null });
      setEditingCatId(null);
      await fetchDevices();
    } catch {
      toast.error('Failed to update category.');
    } finally {
      setSavingCat(false);
    }
  };

  const savePriority = async (id: number, raw: string) => {
    if (savingPri) return;
    const trimmed = raw.trim();
    const parsed = trimmed === '' ? null : parseInt(trimmed, 10);
    if (parsed !== null && (isNaN(parsed) || parsed < 1)) {
      toast.error('Priority must be a whole number ≥ 1.');
      return;
    }
    setSavingPri(true);
    try {
      await patchDeviceField(id, { priority: parsed });
      setEditingPriId(null);
      await fetchDevices();
    } catch {
      toast.error('Failed to update priority.');
    } finally {
      setSavingPri(false);
    }
  };

  const handleDelete = async (id: number, trackingId: string) => {
    if (!window.confirm(`Are you sure you want to delete ${trackingId}? This cannot be undone.`)) return;
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/api/medical-devices/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success("Device deleted successfully");
      fetchDevices();
    } catch (err) {
      toast.error("Failed to delete device");
    }
  };

  const handleViewClick = (device: any) => {
    setViewDevice(device);
  };

  const handleEditClick = (device: any) => {
    setEditingDevice(device);
    setIsModalOpen(true);
  };

  const handleOpenNewModal = () => {
    setEditingDevice(null);
    setIsModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'Functional': return { bg: '#dcfce7', text: '#166534' };
      case 'Rented Out': return { bg: '#dbeafe', text: '#1e40af' };
      case 'In Repair': return { bg: '#ffedd5', text: '#c2410c' };
      case 'Not Working': return { bg: '#fee2e2', text: '#b91c1c' };
      default: return { bg: '#f1f5f9', text: '#475569' };
    }
  };

  // Order: category (A→Z, blank last) → priority (asc, blank last) → name (A→Z).
  const catRank = (d: any) => (d.category ?? '').trim().toLowerCase();
  const filteredDevices = devices
    .filter(device =>
      device.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (device.reference_id || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const ca = catRank(a), cb = catRank(b);
      if (ca !== cb) {
        if (!ca) return 1;
        if (!cb) return -1;
        return ca.localeCompare(cb);
      }
      const pa = a.priority ?? Infinity, pb = b.priority ?? Infinity;
      if (pa !== pb) return pa - pb;
      return a.item_name.localeCompare(b.item_name);
    });

  return (
    <div>
      <style>{globalCss}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', position: 'relative', width: '300px' }}>
          <Search size={18} color="#94a3b8" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)' }} />
          <input 
            type="text" 
            placeholder="Search devices..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', padding: '0.75rem 1rem 0.75rem 2.5rem', borderRadius: '0.5rem', border: '1px solid #cbd5e1', outline: 'none' }}
          />
        </div>
        
        <button onClick={handleOpenNewModal} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: '#0f172a', color: 'white', padding: '0.65rem 1.25rem', borderRadius: '7px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
          <Plus size={16} /> Register Device
        </button>
      </div>

      <div style={{ overflowX: 'auto', backgroundColor: 'white', borderRadius: '9px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
        <table className="ctbl" style={{ width: '100%', borderCollapse: 'collapse', color: '#1d2631' }}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>Sl.No</th>
              <th style={{ width: '60px' }}>Photo</th>
              <th style={{ width: '130px', textAlign: 'left' }}>Device Name</th>
              <th style={{ width: '110px', textAlign: 'left' }}>Company</th>
              <th style={{ width: '80px' }}>HSN Code</th>
              <th style={{ width: '120px' }}>Category</th>
              <th style={{ width: '70px' }}>Priority</th>
              <th style={{ width: '90px' }}>Reference ID</th>
              <th style={{ width: '90px' }}>Purchase Date</th>
              <th style={{ width: '80px' }}>Purchase Cost</th>
              <th style={{ width: '80px' }}>Monthly Rate</th>
              <th style={{ width: '80px' }}>Daily Rate</th>
              <th style={{ width: '60px' }}>GST</th>
              <th style={{ width: '80px' }}>Status</th>
              <th style={{ width: '54px' }}>View</th>
              <th style={{ width: '54px' }}>Edit</th>
              <th style={{ width: '54px' }}>Delete</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
                <tr><td colSpan={17} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
            ) : filteredDevices.length === 0 ? (
                <tr><td colSpan={17} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>No devices found.</td></tr>
            ) : filteredDevices.map((device, index) => {
              const colors = getStatusColor(device.status);
              return (
                <tr 
                  key={device.id} 
                  style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: hoverRowId === device.id ? '#f8fafc' : 'white', transition: 'background 0.1s' }}
                  onMouseEnter={() => setHoverRowId(device.id)}
                  onMouseLeave={() => setHoverRowId(null)}
                >
                  <td style={{ textAlign: 'center', color: '#94a3b8', fontWeight: 600 }}>{index + 1}</td>
                  
                  <td style={{ textAlign: 'center' }}>
                    <ZoomableImage
                      src={device.photo_1_url || PLACEHOLDER_IMG}
                      alt="Device Photo"
                      style={{ width: '45px', height: '45px', borderRadius: '5px', objectFit: 'cover', border: '1px solid #e2e8f0', display: 'block', margin: '0 auto' }}
                      fallbackSrc={PLACEHOLDER_IMG}
                    />
                  </td>
                  
                  <td className="wc" style={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                    {device.item_name}
                  </td>

                  <td className="wc" style={{ color: '#334155' }}>
                    {device.company || '—'}
                  </td>
                  <td style={{ textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>
                    {device.hsn_code || '—'}
                  </td>

                  {/* Category — inline editable */}
                  <td style={{ textAlign: 'center', whiteSpace: 'normal' }} onClick={e => e.stopPropagation()}>
                    {editingCatId === device.id ? (
                      <div style={{ minWidth: 120 }}>
                        <CreatableSelect
                          autoFocus
                          openMenuOnFocus
                          isClearable
                          isDisabled={savingCat}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                          options={categoryOptions.map(cat => ({ value: cat, label: cat }))}
                          defaultValue={device.category ? { value: device.category, label: device.category } : null}
                          placeholder="Select or create…"
                          formatCreateLabel={(input: string) => `+ Create "${input}"`}
                          onChange={(opt: any) => saveCategory(device.id, opt ? opt.value : '')}
                          onCreateOption={(input: string) => saveCategory(device.id, input)}
                          onBlur={() => { if (!savingCat) setEditingCatId(null); }}
                          styles={{
                            menuPortal: base => ({ ...base, zIndex: 9999 }),
                            control: base => ({ ...base, minHeight: 32, fontSize: '0.78rem' }),
                            dropdownIndicator: base => ({ ...base, padding: 4 }),
                            clearIndicator: base => ({ ...base, padding: 4 }),
                          }}
                        />
                      </div>
                    ) : (
                      <span
                        title="Click to set category"
                        onClick={() => setEditingCatId(device.id)}
                        style={{ cursor: 'pointer', color: device.category ? '#0f172a' : '#94a3b8', fontWeight: device.category ? 600 : 400, fontStyle: device.category ? 'normal' : 'italic' }}
                      >
                        {device.category || '— set —'}
                      </span>
                    )}
                  </td>

                  {/* Priority — inline editable */}
                  <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    {editingPriId === device.id ? (
                      <input
                        type="number"
                        min={1}
                        step={1}
                        autoFocus
                        disabled={savingPri}
                        defaultValue={device.priority ?? ''}
                        onKeyDown={e => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          else if (e.key === 'Escape') setEditingPriId(null);
                        }}
                        onBlur={e => savePriority(device.id, e.target.value)}
                        placeholder="#"
                        style={{ width: 52, padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: '0.78rem', textAlign: 'center' }}
                      />
                    ) : (
                      <span
                        title="Click to set priority"
                        onClick={() => setEditingPriId(device.id)}
                        style={{ cursor: 'pointer', color: device.priority != null ? '#0f172a' : '#94a3b8', fontWeight: device.priority != null ? 600 : 400, fontStyle: device.priority != null ? 'normal' : 'italic' }}
                      >
                        {device.priority ?? '— set —'}
                      </span>
                    )}
                  </td>

                  <td style={{ textAlign: 'center', fontFamily: 'monospace', color: '#475569' }}>{device.reference_id || '—'}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDate(device.purchase_date)}</td>
                  <td style={{ textAlign: 'center' }}>{device.purchase_cost != null ? `₹${device.purchase_cost}` : '-'}</td>
                  <td style={{ textAlign: 'center' }}>{device.monthly_rental_rate ? `₹${device.monthly_rental_rate}` : '-'}</td>
                  <td style={{ textAlign: 'center' }}>{device.daily_rental_rate ? `₹${device.daily_rental_rate}` : '-'}</td>
                  <td style={{ textAlign: 'center' }}>{device.gst_applicable || 'N/A'}</td>
                  
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ backgroundColor: colors.bg, color: colors.text, display: 'inline-flex', alignItems: 'center', padding: '2px 6px', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, lineHeight: 1 }}>
                      {device.status}
                    </span>
                  </td>
                  
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => handleViewClick(device)} style={{ background: 'linear-gradient(135deg,#6366f1 0%,#4f46e5 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(79,70,229,0.2)' }}>View</button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => handleEditClick(device)} style={{ background: 'linear-gradient(135deg,#0ea5e9 0%,#2563eb 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(37,99,235,0.2)' }}>Edit</button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => handleDelete(device.id, device.tracking_id)} style={{ background: 'linear-gradient(135deg,#f43f5e 0%,#e11d48 100%)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.35rem 0.7rem', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-block', transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(225,29,72,0.2)' }}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DeviceRegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchDevices}
        editData={editingDevice}
      />

      {viewDevice && (
        <MedicalDeviceDetailsModal
          device={viewDevice}
          onClose={() => setViewDevice(null)}
          onEdit={() => {
            setViewDevice(null);
            setEditingDevice(viewDevice);
            setIsModalOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default MedicalDevicesTab;